// PACKAGE #10 — Cvanto Balance / Coins Foundation.
// Mirrors SubscriptionContext.js's shape/pattern deliberately: one shared
// fetch, refreshed on login/focus, consumed by both the Settings menu badge
// and the Balance page — and, later, Package #11's inline "not enough
// Coins" Boost flow, without either place duplicating the fetch logic.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { getAccessToken } from "../awsAuth";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const WalletContext = createContext({
  loading: true,
  error: null,
  balance: 0,
  updatedAt: null,
  refresh: async () => {},
});

export const WalletProvider = ({ children }) => {
  const { userId, isLoggedIn } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [balance, setBalance] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !userId) {
      setBalance(0);
      setUpdatedAt(null);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const token = await getAccessToken();
      if (!token) throw new Error("No token");

      const res = await fetch(`${API_BASE}/api/wallet/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      setBalance(Number.isFinite(Number(data?.balance)) ? Number(data.balance) : 0);
      setUpdatedAt(data?.updatedAt || null);
      setError(null);
    } catch (e) {
      console.warn("[Wallet] /api/wallet/me failed:", e?.message || e);
      setBalance(0);
      setUpdatedAt(null);
      setError(e?.message || "failed");
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, userId]);

  useEffect(() => {
    if (isLoggedIn === null) return;
    refresh();
  }, [isLoggedIn, userId, refresh]);

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const value = useMemo(
    () => ({ loading, error, balance, updatedAt, refresh }),
    [loading, error, balance, updatedAt, refresh]
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

export const useWallet = () => useContext(WalletContext);
